/**
 * Zustand Store: Security Scan Progress Manager
 *
 * This store manages SSE subscriptions for security scan progress.
 * Unlike React hooks, this store persists across route changes, ensuring
 * that scan processes continue even when the user navigates away.
 *
 * ORGANIZATION SCOPING
 * --------------------
 * A scan is work done on ONE organization's metadata. The store previously held
 * a single scan slot plus a single SSE connection, so the scan the user started
 * in Org A stayed on the Scanner page — and in the header — after switching to
 * Org B, and only cleared on a page refresh.
 *
 * Scans are now partitioned by organization: {@link SecurityScanStore.byOrg}
 * holds one slot (and one SSE connection) per org, and `state` publishes the
 * active org's slot. Every handler is bound to the organization whose scan it is
 * serving, so a completion event for Org A's scan can only ever write into Org
 * A's slot — no ordering assumption, no "is this still current?" check to
 * forget.
 *
 * The connection is NOT torn down on an org switch. The scan is running on the
 * server either way; keeping the stream attached is what lets the user switch
 * back and find it still ticking rather than apparently never started.
 *
 * A subscription ends when:
 * - Scan completes successfully
 * - Scan fails
 * - User explicitly cancels
 * - The user signs out (reset)
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import toast from 'react-hot-toast';
import { getQueryClient } from '@/dataProvider/reactQuery/context';
import { useActivityStore } from '@/stores/activityStore';
import {
  getActiveOrgId,
  isActiveOrg,
  readOrgSlice,
  registerOrgScopedState,
  writeOrgSlice,
  type OrgId,
} from '@/lib/org-scope';
import {
  getOngoingSecurityScanJob,
  startSecurityScan,
  subscribeToSecurityScanStatus,
  type SecurityScanStatus,
} from '@/services/security-scanner';

// ============================================================================
// Types
// ============================================================================

export interface SecurityScanState {
  /** Whether currently scanning */
  isScanning: boolean;
  /** Progress percentage (0-100) */
  progress: number;
  /** Current scan status from SSE */
  status: SecurityScanStatus | null;
  /** Error if scan failed */
  error: Error | null;
  /** Current job ID from API */
  jobId: string | null;
  /** Activity ID for activity store tracking */
  activityId: string | null;
  /** Organization ID */
  orgId: string | null;
  /** User ID */
  userId: string | null;
}

/** One organization's scan, plus the live connection feeding it. */
interface OrgScanSlot {
  state: SecurityScanState;
  /** Non-reactive: closes this org's SSE stream. */
  closeConnection: (() => void) | null;
}

interface SecurityScanStore {
  /** Per-organization scans. Not rendered. */
  byOrg: Map<string, OrgScanSlot>;

  /** The ACTIVE organization's scan — what the Scanner page reads. Read-only. */
  state: SecurityScanState;

  // Actions
  startScan: (orgId: string, userId: string) => Promise<void>;
  rehydrateFromOngoingJob: (orgId: string, userId: string, jobId: string) => void;
  /** Check for an ongoing security scan job (e.g. after retrieval) and connect to SSE if found. */
  checkOngoingJobAndRehydrate: (orgId: string, userId: string) => Promise<void>;
  cancelScan: () => void;
  /**
   * Drop the ACTIVE organization's scan and disconnect its stream.
   *
   * Not called on an org switch — see the note at the top of this file. This is
   * for sign-out and for the user explicitly discarding a finished scan.
   */
  reset: () => void;

  // Internal actions — all addressed by the organization they belong to
  _slot: (orgId: OrgId | null) => OrgScanSlot;
  _writeSlot: (orgId: OrgId | null, slot: OrgScanSlot) => void;
  _patchState: (orgId: OrgId | null, updates: Partial<SecurityScanState>) => void;
  _handleProgress: (orgId: OrgId | null, scanStatus: SecurityScanStatus) => void;
  _handleComplete: (orgId: OrgId | null, scanStatus: SecurityScanStatus) => void;
  _handleError: (orgId: OrgId | null, error: Error) => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: SecurityScanState = {
  isScanning: false,
  progress: 0,
  status: null,
  error: null,
  jobId: null,
  activityId: null,
  orgId: null,
  userId: null,
};

function emptySlot(): OrgScanSlot {
  return { state: initialState, closeConnection: null };
}

// ============================================================================
// Store
// ============================================================================

export const useSecurityScanStore = create<SecurityScanStore>()(
  devtools(
    (set, get) => ({
      byOrg: new Map(),
      state: initialState,

      _slot: (orgId) => readOrgSlice(get().byOrg, orgId, emptySlot),

      _writeSlot: (orgId, slot) => {
        set((store) => {
          const byOrg = writeOrgSlice(store.byOrg, orgId, slot);
          return isActiveOrg(orgId) ? { byOrg, state: slot.state } : { byOrg };
        });
      },

      _patchState: (orgId, updates) => {
        const slot = get()._slot(orgId);
        get()._writeSlot(orgId, { ...slot, state: { ...slot.state, ...updates } });
      },

      startScan: async (orgId, userId) => {
        const slot = get()._slot(orgId);

        // Don't start if already scanning
        if (slot.state.isScanning) {
          console.log('🛡️ Already scanning, ignoring new scan request');
          return;
        }

        // If backend already has an ongoing job, rehydrate instead of starting a new one
        try {
          const ongoing = await getOngoingSecurityScanJob({ org_id: orgId, user_id: userId });
          if (ongoing?.job_id) {
            console.log('🛡️ Found ongoing job, rehydrating:', ongoing.job_id);
            get().rehydrateFromOngoingJob(orgId, userId, ongoing.job_id);
            return;
          }
        } catch (_) {
          // No ongoing job or API error; proceed to start new scan
        }

        // Cleanup any existing connection for THIS org
        if (slot.closeConnection) {
          console.log('🔌 Closing existing SSE connection before new scan');
          slot.closeConnection();
        }

        // Generate activity ID
        const activityId = `security_scan_${Date.now()}`;

        // Set initial state
        get()._writeSlot(orgId, {
          closeConnection: null,
          state: {
            ...initialState,
            isScanning: true,
            orgId,
            userId,
            activityId, // Store activity ID for later updates
            jobId: null, // Will be set after API response
          },
        });

        // Track activity — tagged with the org that is active right now, which is
        // the org this scan was started for.
        useActivityStore.getState().startActivity(
          activityId,
          'security_scan',
          'Starting security scan...'
        );

        try {
          console.log('🛡️ Starting security scan for org:', orgId);

          // Start the scan via API
          const response = await startSecurityScan({ org_id: orgId, user_id: userId });

          console.log('✅ Scan started, job_id:', response.job_id);

          // Update job ID
          get()._patchState(orgId, { jobId: response.job_id });

          // Subscribe to SSE updates. Every handler is bound to `orgId`, so a
          // frame arriving after the user has switched away updates this org's
          // slot rather than whatever is on screen.
          const closeConnection = subscribeToSecurityScanStatus(
            {
              job_id: response.job_id,
              org_id: orgId,
              user_id: userId,
            },
            (scanStatus) => {
              const currentStore = get();

              if (scanStatus.status === 'completed') {
                currentStore._handleComplete(orgId, scanStatus);
              } else if (scanStatus.status === 'failed') {
                const errorMsg = new Error(scanStatus.error_message || 'Scan failed');
                currentStore._handleError(orgId, errorMsg);
              } else {
                currentStore._handleProgress(orgId, scanStatus);
              }
            },
            (error) => {
              get()._handleError(orgId, error);
            },
            () => {
              // onComplete callback from SSE (connection closed)
              console.log('🔌 SSE connection closed');
            }
          );

          get()._writeSlot(orgId, { ...get()._slot(orgId), closeConnection });

        } catch (error) {
          console.error('❌ Failed to start security scan:', error);
          get()._handleError(orgId, error as Error);

          // Fail activity
          useActivityStore.getState().failActivity(activityId, (error as Error).message);
          useActivityStore.getState().removeActivity(activityId);
        }
      },

      rehydrateFromOngoingJob: (orgId, userId, jobId) => {
        const slot = get()._slot(orgId);
        if (slot.state.isScanning && slot.state.jobId === jobId) {
          return;
        }
        if (slot.closeConnection) {
          slot.closeConnection();
        }
        const activityId = `security_scan_rehydrate_${jobId}`;
        get()._writeSlot(orgId, {
          closeConnection: null,
          state: {
            ...initialState,
            isScanning: true,
            orgId,
            userId,
            activityId,
            jobId,
            error: null,
          },
        });
        useActivityStore.getState().startActivity(
          activityId,
          'security_scan',
          'Resuming security scan...'
        );
        const closeConnection = subscribeToSecurityScanStatus(
          { job_id: jobId, org_id: orgId, user_id: userId },
          (scanStatus) => {
            const currentStore = get();
            if (scanStatus.status === 'completed') {
              currentStore._handleComplete(orgId, scanStatus);
            } else if (scanStatus.status === 'failed') {
              currentStore._handleError(orgId, new Error(scanStatus.error_message || 'Scan failed'));
            } else {
              currentStore._handleProgress(orgId, scanStatus);
            }
          },
          (error) => get()._handleError(orgId, error),
          () => { console.log('🔌 SSE connection closed'); }
        );
        get()._writeSlot(orgId, { ...get()._slot(orgId), closeConnection });
      },

      checkOngoingJobAndRehydrate: async (orgId, userId) => {
        try {
          const ongoing = await getOngoingSecurityScanJob({ org_id: orgId, user_id: userId });
          if (ongoing?.job_id) {
            get().rehydrateFromOngoingJob(orgId, userId, ongoing.job_id);
          }
        } catch {
          // No ongoing job or API error; nothing to rehydrate
        }
      },

      _handleProgress: (orgId, scanStatus) => {
        const store = get();

        // Update activity store using the activityId (not jobId)
        const activityId = store._slot(orgId).state.activityId;
        if (activityId) {
          useActivityStore.getState().updateActivity(activityId, {
            progress: scanStatus.progress.percent,
            message: `Scanning... ${scanStatus.progress.current}/${scanStatus.progress.total}`,
          });
        }

        store._patchState(orgId, {
          progress: scanStatus.progress.percent,
          status: scanStatus,
        });
      },

      _handleComplete: (orgId, scanStatus) => {
        const store = get();
        const slot = store._slot(orgId);
        const activityId = slot.state.activityId;

        console.log('✅ Security scan completed:', scanStatus);

        if (activityId) {
          useActivityStore.getState().completeActivity(
            activityId,
            `Found ${scanStatus.progress.total} items`
          );
          useActivityStore.getState().removeActivity(activityId);
        }

        store._writeSlot(orgId, {
          closeConnection: null,
          state: {
            ...slot.state,
            isScanning: false,
            progress: 100,
            status: scanStatus,
          },
        });

        // Only interrupt the user for a scan of the org they are looking at. A
        // toast about Org A while they are working in Org B is noise at best and
        // misleading at worst — it reads as if Org B had just been scanned.
        if (isActiveOrg(orgId)) {
          toast.success(`Scan complete! Found ${scanStatus.progress.total} items.`, {
            duration: 3000,
            position: 'top-right',
          });
        }

        slot.closeConnection?.();

        // These keys carry the org id, so this refreshes the scanned org's data
        // whether or not it is on screen.
        const qc = getQueryClient();
        qc.invalidateQueries({ queryKey: ['securityHealth'] });
        qc.invalidateQueries({ queryKey: ['securityFindings'] });
      },

      _handleError: (orgId, error) => {
        const store = get();
        const slot = store._slot(orgId);
        const activityId = slot.state.activityId;

        console.error('❌ Security scan failed:', error);

        // Fail activity in global store
        if (activityId) {
          useActivityStore.getState().failActivity(activityId, error.message);
          useActivityStore.getState().removeActivity(activityId);
        }

        store._writeSlot(orgId, {
          closeConnection: null,
          state: {
            ...slot.state,
            isScanning: false,
            error,
            status: slot.state.status ? { ...slot.state.status, status: 'failed' } : null,
          },
        });

        if (isActiveOrg(orgId)) {
          toast.error(error.message || 'Security scan failed', {
            duration: 4000,
            position: 'top-right',
          });
        }

        // Close SSE connection
        slot.closeConnection?.();
      },

      cancelScan: () => {
        const orgId = getActiveOrgId();
        const slot = get()._slot(orgId);
        const activityId = slot.state.activityId;

        console.log('🛑 Cancelling security scan');

        slot.closeConnection?.();

        // Remove activity
        if (activityId) {
          useActivityStore.getState().removeActivity(activityId);
        }

        get()._writeSlot(orgId, emptySlot());
      },

      reset: () => {
        const orgId = getActiveOrgId();
        const slot = get()._slot(orgId);

        slot.closeConnection?.();
        if (slot.state.activityId) {
          // The old implementation closed the stream but left the activity behind,
          // which is precisely why the header kept showing a scanner that was no
          // longer running until the page was refreshed.
          useActivityStore.getState().removeActivity(slot.state.activityId);
        }

        get()._writeSlot(orgId, emptySlot());
      },
    }),
    { name: 'security-scan-store' }
  )
);

// ============================================================================
// Organization scoping
// ============================================================================

/** Re-point `state` at the newly active organization's scan. Nothing is stopped. */
registerOrgScopedState({
  name: 'securityScanStore',
  onOrgChange: (nextOrgId) => {
    useSecurityScanStore.setState((store) => ({
      state: readOrgSlice(store.byOrg, nextOrgId, emptySlot).state,
    }));
  },
  onSignOut: shutdownAllSecurityScans,
});

/** Disconnect every organization's scan. Sign-out only. */
export function shutdownAllSecurityScans(): void {
  const { byOrg } = useSecurityScanStore.getState();
  for (const slot of byOrg.values()) slot.closeConnection?.();
  useSecurityScanStore.setState({ byOrg: new Map(), state: initialState });
}

// ============================================================================
// Selectors
// ============================================================================

export const selectSecurityScanState = (store: SecurityScanStore) => store.state;
export const selectIsScanning = (store: SecurityScanStore) => store.state.isScanning;
export const selectScanProgress = (store: SecurityScanStore) => store.state.progress;
export const selectScanStatus = (store: SecurityScanStore) => store.state.status;
